import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ShieldCheck, ArrowLeft, AlertTriangle } from 'lucide-react';

// COPPA VPC start page. Parent enters name+email, we create a Stripe $0.01
// checkout session, then redirect to Stripe. On success Stripe redirects to
// /apex/coppa/verify/success (rendered by CoppaVerifySuccess.jsx).
export default function CoppaVerify() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const start = async (e) => {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      const r = await api.post('/apex/coppa/verify-start', {
        parent_name: name.trim(),
        parent_email: email.trim(),
      });
      if (r.data.already_ok) {
        navigate('/apex/course');
        return;
      }
      // Stripe hosted checkout — hand off to Stripe.
      window.location.href = r.data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to start verification');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="apex-coppa-page">
      <div className="max-w-lg mx-auto px-6 py-10">
        <button onClick={() => navigate('/apex/course')}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-2 mb-6"
                data-testid="coppa-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-start gap-3 mb-6">
          <ShieldCheck className="w-8 h-8 text-amber-400 flex-shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl font-black mb-1">Parental Consent Verification</h1>
            <p className="text-sm text-gray-400">
              Under the Children&apos;s Online Privacy Protection Act (COPPA), parents must verify consent for users under 13 before public-profile features or community posting are unlocked.
            </p>
          </div>
        </div>

        <Card className="bg-[#111] border border-gray-800/60 mb-4">
          <CardContent className="p-5">
            <h2 className="text-sm font-bold uppercase tracking-widest text-orange-500 mb-3">How it works</h2>
            <ol className="text-sm text-gray-300 space-y-2 list-decimal list-inside">
              <li>Enter the verifying parent&apos;s name and email below.</li>
              <li>Stripe will charge <span className="font-bold text-white">$0.01</span> to the parent&apos;s card.</li>
              <li>The $0.01 is <span className="font-bold text-white">automatically refunded</span> within 24 hours.</li>
              <li>Your student&apos;s account is unlocked immediately after the charge clears.</li>
            </ol>
            <p className="text-xs text-gray-500 mt-3">
              The card must belong to the parent or legal guardian. This is an FTC-approved Verifiable Parental Consent method.
            </p>
          </CardContent>
        </Card>

        <form onSubmit={start} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-400 mb-1 block">Parent Full Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Firstname Lastname"
              className="bg-black border-gray-700 text-white"
              data-testid="coppa-parent-name-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-400 mb-1 block">Parent Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="parent@example.com"
              className="bg-black border-gray-700 text-white"
              data-testid="coppa-parent-email-input"
            />
          </div>

          {err && (
            <div className="flex items-start gap-2 p-3 rounded border border-red-500/40 bg-red-500/5" data-testid="coppa-error">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-300">{err}</p>
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting || !name.trim() || !email.trim()}
            className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold h-11"
            data-testid="coppa-start-btn"
          >
            {submitting ? 'Preparing checkout…' : 'Continue to $0.01 Verification →'}
          </Button>
        </form>
      </div>
    </div>
  );
}
